import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useLanguage } from '../context/LanguageContext'
import { useTenant } from '../context/TenantContext'
import InquiryService from '../services/inquiry.service'
import Loader from '../components/ui/loader'
import NotFound from './NotFound'
import {
  InfoIcon,
  DollarSign,
  SpinWheel,
  WindowPageIcon,
  DoubleCard,
  UserProfileIcon,
  SpeakerIcon,
  MailDocumentIcon,
} from '../components/icons'
import { useClient } from '../context/ClientContext'
import { isCustomerInquiryEnabled } from '../utils/tenantFeatures'

// Icon mapping for dynamic icon resolution
const iconMap = {
  DollarSign,
  SpinWheel,
  WindowPageIcon,
  DoubleCard,
  UserProfileIcon,
  SpeakerIcon,
  MailDocumentIcon,
}

const InquiryFormMenu = () => {
  const { t, languageCode } = useLanguage()
  const navigate = useNavigate()
  const { tenanturlname } = useParams()
  const { isAuthenticated } = useClient()
  const { tenant } = useTenant()
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchCategories = async () => {
      if (!isCustomerInquiryEnabled(tenant)) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const schemas = await InquiryService.getInquiryForms(
          tenant.tenantid,
          languageCode,
        )

        if (schemas && schemas.inquiryCategories) {
          // Map icon strings to actual icon components
          const categoriesWithIcons = schemas.inquiryCategories.map(
            (category) => ({
              ...category,
              icon: iconMap[category.icon] || InfoIcon,
            }),
          )

          // Filter categories based on auth status
          const filteredCategories = categoriesWithIcons.filter((category) => {
            // If category requires auth and user is not authenticated, hide it
            if (category.requireAuth && !isAuthenticated) {
              return false
            } else if (category.publicOnly && isAuthenticated) {
              return false
            }
            return true
          })

          setCategories(filteredCategories)
        }
      } catch (error) {
        console.error('Error fetching inquiry categories:', error)
      } finally {
        setLoading(false)
      }
    }

    if (tenant?.tenantid) {
      fetchCategories()
    }
  }, [tenant, isAuthenticated, languageCode])

  const handleCategorySelect = (categoryId) => {
    navigate(`/${tenanturlname}/inquiry/category/${categoryId}`)
  }

  const campaignClosedNotice = t('inquiry.campaignClosedNotice')

  if (loading) {
    return (
      <Loader
        fullscreen
        label={t('common.loading')}
        size="xl"
      />
    )
  }

  if (tenant && !isCustomerInquiryEnabled(tenant)) {
    return <NotFound />
  }

  return (
    <>
      <div className="inquiry-form-page">
        {/* Inquiry Form - START */}
        <div className="flex flex-col items-center w-full mx-auto mt-8 px-4">
          {/* Header */}
          <div className="flex gap-2 w-full mb-6 justify-start">
            <InfoIcon className="w-6 h-6" />
            <h1 className="text-base font-extrabold text-theme-secondary">
              {t('inquiry.submitAnInquiry')}
            </h1>
          </div>

          {campaignClosedNotice && (
            <div className="w-full mb-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-medium leading-6 text-theme-secondary whitespace-pre-line">
              {campaignClosedNotice}
            </div>
          )}

          {/* Inquiry Options Card */}
          <div className="w-full bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            {categories.length === 0 ? (
              <div className="py-8 px-5 text-center text-gray-500">
                <p>{t('inquiry.noInquiryCategoriesAvailable')}</p>
              </div>
            ) : (
              categories.map((category) => {
                const IconComponent = category.icon
                return (
                  <div
                    key={category.id}
                    onClick={() => handleCategorySelect(category.id)}
                    className="flex items-start justify-between py-4 px-5 cursor-pointer hover:bg-theme-primary/10 border-b border-[hsla(0,0%,85%,1)] last:border-b-0 "
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-5 h-5 text-theme-primary ">
                        <IconComponent className="md:w-[20px] md:h-[20px] transition-colors duration-200" />
                      </span>
                      <span className="text-sm font-medium text-theme-secondary">
                        {t(category.label)}
                      </span>
                    </div>
                    <span className="text-blue-600 text-xl">›</span>
                  </div>
                )
              })
            )}
          </div>
        </div>
        {/* Inquiry Form - END */}
      </div>
    </>
  )
}

export default InquiryFormMenu
